import React from 'react'
import {debounce} from 'lodash'
import Dialog from 'material-ui-legacy/Dialog'
import AutoComplete from 'material-ui-legacy/AutoComplete'
import ContentAddCircleOutline from 'material-ui-legacy/svg-icons/content/add-circle-outline'
import ContentLink from 'material-ui-legacy/svg-icons/content/link'
import {deepOrange500, lightGreenA700} from 'material-ui-legacy/styles/colors'

import errors from '../../lib/errors.js'
import CancelButton from './CancelButton'
import ErrorDialog from './ErrorDialog'
import CircularLoader from './CircularLoader'

/*
Props:
- name: field name
- value: text value for the field
- selectedId: id value for the field
- dataStore: a Store component to be used to get autocomplete matches
- autoCompleteFilter: filter that will receive the field value
- additionalFilters: Object - a filter to be merged with autoCompleteFilter
  Example: filtering AgencyProducers by {agencyId: id} + nameAutoComplete
- autoCompleteAttribute: store attribute use as autocomplete data
- validatedOnChange: callback function for onChange, but including a
  validation flag and field name:
  callback(selectedId, Bool(isValid), this.props.name)
- onTextChange: plain callback with no validation, receives text value:
  callback(textValue)
- formComponent: we absolutely must have a match, so we're providing a form to
  create a new item so the database relationship can be satisfied.
- required: boolean (false)
- errorMessage: Error message to be displayed (can be set by external form
  validation)
- debounceTime: throttling for autocomplete data requests. Default: 250ms
- maxSearchResults: Maximum results to show in dropdown. Default: 10
- formCloseEvent: document Event to listen for to close the add form dialog.
  Note this is supposed to be the event *object*, not the name.
- openOnFocus: open autocomplete dropdown on focus

Other props are the same as for a regular TextField and will be passed along.
*/
class RelationalTextInput extends React.PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      value: (this.props.value || ''),
      selectedId: (this.props.selectedId || ''),
      dataSource: [],
      displayAddForm: false,
      error: '',
      errorMessage: (this.props.errorMessage || ''),
      loading: false
    }

    const debounceTime = this.props.debounceTime || 250
    this._loadData = debounce(this._loadData, debounceTime)
    this.maxSearchResults = this.props.maxSearchResults || 10
  }

  componentDidMount() {
    if (this.props.formCloseEvent) {
      document.addEventListener(
        this.props.formCloseEvent.type,
        this._closeAddForm
      )
    }
  }

  componentWillUnmount() {
    if (this.props.formCloseEvent) {
      document.removeEventListener(
        this.props.formCloseEvent.type,
        this._closeAddForm
      )
    }
  }

  _loadData = () => {
    this.setState({loading: true})
    const DataStore = this.props.dataStore
    let filter = {}
    filter[this.props.autoCompleteFilter] = this.state.value

    // Adds custom additional filters if present
    if (this.props.additionalFilters) Object.assign(filter, this.props.additionalFilters)

    DataStore.findAll(filter, [], 1, this.maxSearchResults)
    .then((result) => {
      const dataSource = result.data.map((item) => {
        return {
          text: item.attributes[this.props.autoCompleteAttribute],
          value: item.id
        }
      })
      this.setState({dataSource: dataSource, loading: false})
    })
    .catch((error) => {
      this.setState({error: errors.unpack(error), loading: false})
    })
  }

  _change = (value) =>
  {
    this.setState(
      {value: value, selectedId: ''},
      () => {
        this._loadData()
        if (this.props.onTextChange) this.props.onTextChange(value)
      }
    )
  }

  _chosen = (chosenValue) =>
  {
    if (typeof(chosenValue) !== 'object') {
      if (this.props.required) {
        this.setState({
          selectedId: '',
          errorMessage: 'You must provide a match. Click the + icon to create a new item.'
        })
      }
    }
    else {
      this.setState({
        selectedId: chosenValue.value,
        errorMessage: ''
      },
      () => {
        this.props.validatedOnChange(
          chosenValue.value,
          (this.props.required ? Boolean(this.state.selectedId) : true),
          this.props.name
        )
      })
    }
  }

  _displayAddForm = () => this.setState({displayAddForm: true})

  _closeAddForm = () => this.setState({
    displayAddForm: false,
    errorMessage: '',
    value: this.props.value,           // Gotta refresh those props!
    selectedId: this.props.selectedId  // See any form using this component
  }, this.forceUpdate)

  _closeErrorDialog = () => this.setState({error: ''})

  render() {
    // Component positioning
    const loaderStyle = {
      display: 'inline-block',
      textAlign: 'right',
      float: 'right',
      marginBottom: '0',
      marginTop: '35px'
    }
    const addLinkStyle = {
      display: 'inline-block',
      cursor: 'pointer',
      textAlign: 'right',
      float: 'right',
      marginBottom: '0',
      marginTop: '35px'
    }
    const linkedStyle = {
      float: 'right',
      marginTop: '-35px'
    }
    let autoCompleteStyle = this.props.style || {}
    if (!this.state.selectedId) {
      Object.assign(
        autoCompleteStyle, {display: 'inline-block', width: '90%'}
      )
    }

    // Form dialog configuration
    const formDialogActions = [<CancelButton onTouchTap={this._closeAddForm} />]

    return (
      <div>
        {!Boolean(this.state.selectedId) && !this.state.loading &&
          <ContentAddCircleOutline
            style={addLinkStyle}
            color={deepOrange500}
            onTouchTap={this._displayAddForm}
           />
        }
        {!Boolean(this.state.selectedId) && this.state.loading &&
          <CircularLoader size={24} style={loaderStyle} />
        }
        <AutoComplete
          style={autoCompleteStyle}
          hintText={this.props.hintText}
          filter={AutoComplete.noFilter}
          floatingLabelText={this.props.floatingLabelText}
          dataSource={this.state.dataSource}
          onUpdateInput={this._change}
          onNewRequest={this._chosen}
          fullWidth={this.props.fullWidth}
          maxSearchResults={this.maxSearchResults}
          searchText={this.state.value}
          errorText={this.state.errorMessage}
          openOnFocus={this.props.openOnFocus}
        />
        {Boolean(this.state.selectedId) &&
          <ContentLink style={linkedStyle} color={lightGreenA700} />
        }

        {Boolean(this.state.error) &&
          <ErrorDialog
            open={Boolean(this.state.error)}
            closeCallback={this._closeErrorDialog}
            title="Couldn't read autocomplete data">
            <p>
              <strong>Error:</strong> {this.state.error}
            </p>
          </ErrorDialog>
        }

        {/* Add Form */}
        <Dialog
          open={this.state.displayAddForm}
          actions={formDialogActions}>
          {this.props.formComponent}
        </Dialog>

      </div>
    )
  }
}

export default RelationalTextInput



// WEBPACK FOOTER //
// ./src/components/UI/RelationalTextInput.jsx